/**
 * KycTrafficLight — at-a-glance progress across the three Sandbox KYC
 * milestones. Reads /api/kyc/sandbox/status which returns aggregate
 * flags + per-check records, then renders three "lights":
 *
 *   Founder ID   – PAN + DigiLocker Aadhaar
 *   Company      – CIN + GSTIN + Company PAN
 *   Directors    – ≥1 DIN verified
 *
 * Each light is green (done), amber (partial), or grey (not started).
 * Clicking anywhere on the card deep-links to /startup/kyc.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertCircle, Circle, ArrowRight, ShieldCheck } from "lucide-react";
import api from "../../lib/api";
import { Card } from "../common/Primitives";

const TONES = {
  green: { fg: "text-emerald-700", bg: "bg-emerald-500", ring: "ring-emerald-200" },
  amber: { fg: "text-amber-700",   bg: "bg-amber-400",   ring: "ring-amber-200" },
  grey:  { fg: "text-ink-muted",   bg: "bg-navy/20",     ring: "ring-navy/10" },
};

function lightFor(done, partial) {
  if (done) return "green";
  if (partial) return "amber";
  return "grey";
}

function ProgressDot({ tone, testId }) {
  const t = TONES[tone];
  return (
    <span
      className={`inline-block h-3 w-3 rounded-full ${t.bg} ring-4 ${t.ring}`}
      data-testid={testId}
      data-tone={tone}
    />
  );
}

function iconFor(tone) {
  if (tone === "green") return <CheckCircle2 size={14} className="text-emerald-600" />;
  if (tone === "amber") return <AlertCircle size={14} className="text-amber-600" />;
  return <Circle size={14} className="text-ink-muted" />;
}

export default function KycTrafficLight() {
  const [state, setState] = useState({ loading: true });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/kyc/sandbox/status");
      setState({ loading: false, ...data });
    } catch {
      setState({ loading: false, error: true });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (state.loading || state.error) return null;
  if (!state.sandbox_enabled) return null;

  const c = state.checks || {};
  const s = (rec) => (rec?.status || "").toLowerCase() === "succeeded";

  // Founder Identity
  const idDone = state.identity_verified;
  const idPartial = !idDone && (s(c.pan) || s(c.digilocker));
  const idTone = lightFor(idDone, idPartial);

  // Company (all 3 pass)
  const coParts = [s(c.company_pan), s(c.cin), s(c.gstin)];
  const coDone = coParts.every(Boolean);
  const coPartial = !coDone && coParts.some(Boolean);
  const coTone = lightFor(coDone, coPartial);

  // Directors — parsed lists any verified DINs
  const dinRec = c.din;
  const dinDone = s(dinRec);
  const dinPartial = !dinDone && Boolean(dinRec);
  const dinTone = lightFor(dinDone, dinPartial);

  const rows = [
    {
      key: "founder",
      title: "Founder ID",
      caption: idDone ? "Aadhaar + PAN verified" : idPartial ? "One of Aadhaar / PAN done" : "Aadhaar + PAN pending",
      tone: idTone,
      chips: [
        { label: "PAN",       ok: s(c.pan) },
        { label: "DigiLocker", ok: s(c.digilocker) },
      ],
    },
    {
      key: "company",
      title: "Company",
      caption: coDone ? "CIN + GSTIN + PAN verified" : coPartial ? "Partial — more checks needed" : "Company checks pending",
      tone: coTone,
      chips: [
        { label: "CIN",   ok: s(c.cin) },
        { label: "GSTIN", ok: s(c.gstin) },
        { label: "PAN",   ok: s(c.company_pan) },
      ],
    },
    {
      key: "directors",
      title: "Directors",
      caption: dinDone ? "Director DIN verified" : dinPartial ? "DIN in progress" : "No DIN verified",
      tone: dinTone,
      chips: [
        { label: "DIN", ok: dinDone },
      ],
    },
  ];

  return (
    <Card testId="kyc-traffic-light" className="relative">
      <Link
        to="/startup/kyc"
        className="absolute inset-0 rounded-2xl focus:outline-none focus:ring-2 focus:ring-gold/50"
        aria-label="Open KYC page"
        data-testid="kyc-traffic-light-link"
      />
      <div className="relative pointer-events-none">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline flex items-center gap-1">
              <ShieldCheck size={12} /> KYC PROGRESS
            </div>
            <div className="font-heading text-ink text-lg mt-0.5">Verification at a glance</div>
          </div>
          <ArrowRight className="text-ink-muted" size={16} />
        </div>

        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.key} className="flex items-center gap-3" data-testid={`kyc-tl-row-${r.key}`}>
              <ProgressDot tone={r.tone} testId={`kyc-tl-dot-${r.key}`} />
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-heading text-ink flex items-center gap-1.5`}>
                  {r.title} {iconFor(r.tone)}
                </div>
                <div className="text-xs text-ink-soft truncate">{r.caption}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {r.chips.map((chip) => (
                  <span
                    key={chip.label}
                    className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${chip.ok ? "bg-emerald-100 text-emerald-800" : "bg-navy/[0.05] text-ink-muted"}`}
                    data-testid={`kyc-tl-chip-${r.key}-${chip.label.toLowerCase()}`}
                    data-ok={chip.ok ? "true" : "false"}
                  >
                    {chip.label}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {!idDone && (
          <div className="mt-4 text-xs text-ink-faint">
            Complete Aadhaar & PAN first — company and director checks unlock instantly after.
          </div>
        )}
      </div>
    </Card>
  );
}
